import { useState } from "react";
import { Link } from "react-router-dom";
import { Cross, Dumbbell, Compass, Coins, ArrowRight, BookOpen, Users, HandHeart } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import heroImg from "@/assets/coaching-hero-book.jpg";
import progressionImg from "@/assets/landing/coaching-progression.jpg";
import lanceImg from "@/assets/lance-profile.jpg";

const LIFE = [
  { icon: Cross, title: "Spiritual", text: "A life rooted in Scripture, prayer and purpose." },
  { icon: Dumbbell, title: "Physical", text: "A stronger body. More energy for the life ahead." },
  { icon: Compass, title: "Mental", text: "A clearer mind. Habits that give your days direction." },
  { icon: Coins, title: "Financial", text: "Wiser stewardship. Greater freedom to build and give." },
];
const JOURNEY = ["Begin", "Awaken", "Practice", "Build", "Strengthen", "Lead", "Multiply", "Leave a legacy"];

export default function CoachingLanding() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("landing_leads").insert({
      email,
      name: form.name.trim().slice(0, 120) || null,
      message: form.message.trim().slice(0, 2000) || null,
      source: "coaching_contact",
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
    });
    setBusy(false);
    if (error && !/duplicate/i.test(error.message)) {
      toast.error("Something went wrong", { description: error.message });
      return;
    }
    setSent(true);
    toast.success("Request received — Lance will reach out.");
  };

  return (
    <div className="font-serif overflow-x-hidden [--primary:var(--gold)] [--ring:var(--gold)]">
      <header className="border-b border-primary/30 px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center md:justify-between gap-3 py-6">
          <p className="text-2xl md:text-3xl text-primary">DECORUM LIFESTYLE</p>
          <nav aria-label="Main navigation" className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-3 text-[11px] uppercase text-foreground">
            <a href="#coach" className="hover:text-primary">Your coach</a>
            <a href="#program" className="hover:text-primary">The journey</a>
            <a href="#remnant" className="hover:text-primary">Our mission</a>
            <Link to="/auth" className="hover:text-primary">Member Sign In</Link>
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <img src={heroImg} alt="The Decorum book standing upright on a marble ledge beside a column" width={1920} height={912} className="h-64 w-full object-cover object-[72%_center] md:absolute md:inset-0 md:h-full md:w-full md:object-right" />
        <div className="hidden md:block absolute inset-0 md:bg-gradient-to-r md:from-background md:via-background/85 md:via-30% md:to-transparent md:to-55%" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="max-w-lg">
            <h1 className="text-4xl md:text-6xl leading-tight text-primary">What Could<br />Your Life Become?</h1>
            <p className="mt-5 text-lg leading-relaxed text-foreground">A gamified formation program coached by Lance Ehlers — level up spiritually, physically, mentally and financially by living the Decorum Lifestyle.</p>
            <Button asChild size="lg" className="mt-8 uppercase rounded-sm">
              <a href="#contact">Begin the Journey <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
            <p className="mt-6 text-xs uppercase text-muted-foreground">Discipline · Purpose · A better tomorrow</p>
          </div>
        </div>
      </section>

      <section id="method" className="px-6 py-14 md:py-20 border-b border-primary/20">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs uppercase text-primary">The life you're called to</p>
          <h2 className="mt-3 text-center text-3xl md:text-4xl text-foreground">More than a new routine.<br className="sm:hidden" /> A new direction.</h2>
          <p className="max-w-3xl mx-auto mt-5 text-center text-lg leading-relaxed text-muted-foreground">Imagine waking with purpose, training with intention, putting your money in order and growing in faith. With Lance as your coach, you work toward a life that has room for both personal growth and serving others.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 mt-10">
            {LIFE.map((front) => (
              <div key={front.title} className="text-center">
                <front.icon className="mx-auto h-7 w-7 text-primary" strokeWidth={1.3} />
                <h3 className="mt-3 text-lg text-primary">{front.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{front.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="coach" className="px-6 py-14 md:py-20 border-b border-primary/20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-[1fr_320px] lg:grid-cols-[1fr_360px] gap-10 items-center">
          <div>
            <p className="text-xs uppercase text-primary">Your coach</p>
            <h2 className="mt-3 text-4xl text-foreground">Lance Ehlers</h2>
            <p className="mt-2 text-sm text-primary">Author · Founder · Coach</p>
            <div className="mt-6 grid lg:grid-cols-2 gap-8">
              <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
                <p>Lance Ehlers is the author and founder of Decorum Lifestyle. He graduated from Liberty University with a Bachelor of Science in Psychology, concentrating in Life Coaching.</p>
                <p>His studies in behavioral science and psychological theory shaped an approach that integrates evidence-based psychological principles with biblical teachings.</p>
              </div>
              <blockquote className="border-l border-primary/40 pl-6">
                <p className="text-2xl text-primary">Awaken</p>
                <p className="mt-3 italic leading-relaxed text-foreground">“One man awake, awakens another. The second awakens his next-door brother. The three awake can rouse a town, by turning the whole place upside down. The many awake can make such a fuss, it finally awakens the rest of us. One man up, with dawn in his eyes, surely then multiplies.”</p>
                <footer className="mt-4 text-xs text-muted-foreground">Lawrence Tribble · Lance's favorite poem</footer>
              </blockquote>
            </div>
          </div>
          <img src={lanceImg} alt="Lance Ehlers, author and founder of Decorum Lifestyle" loading="lazy" className="w-full max-w-sm mx-auto aspect-[4/5] object-cover object-top" />
        </div>
      </section>

      <section id="program" className="py-14 md:py-20 border-b border-primary/20">
        <div className="px-6 text-center max-w-7xl mx-auto">
          <p className="text-xs uppercase text-primary">How you level up</p>
          <h2 className="mt-3 text-3xl md:text-4xl text-foreground">Become the person who lives it.</h2>
          <p className="mt-5 max-w-3xl mx-auto text-lg leading-relaxed text-muted-foreground">Begin with the book in Stage 1. Put what you learn into practice. As you build consistency, your avatar reflects the journey — from carrying the weight to walking with purpose to standing ready to lead.</p>
        </div>
        <div className="mt-8 max-w-[1600px] mx-auto overflow-x-auto" tabIndex={0} role="region" aria-label="Eight-stage visual progression">
          <div className="min-w-[840px]">
            <img src={progressionImg} alt="The same man progressing through eight stages: carrying a backpack, walking with a stick, practicing discipline and becoming a Roman soldier and leader" width={1920} height={640} loading="lazy" className="w-full h-auto" />
            <ol className="grid grid-cols-8 px-5 pb-6 border-b border-primary/30">
              {JOURNEY.map((label, i) => (
                <li key={label} className="text-center px-1">
                  <span className="block text-xs text-primary">Stage {i + 1}</span>
                  <span className="block mt-1 text-sm text-foreground">{label}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="px-6 max-w-7xl mx-auto mt-10 grid md:grid-cols-3 gap-8">
          <div>
            <BookOpen className="h-6 w-6 text-primary" strokeWidth={1.3} />
            <h3 className="mt-3 text-xl text-foreground">Live it in the real world</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">Prayer, study, training and financial discipline become daily practices. Track change from your starting point, not someone else's finish line.</p>
          </div>
          <div>
            <Compass className="h-6 w-6 text-primary" strokeWidth={1.3} />
            <h3 className="mt-3 text-xl text-foreground">Grow in the Simulation</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">Earn XP through real-world action. Build your foundation, learn in the Academy, practice at the Trade Desk and explore business and real estate in the Deal Room.</p>
          </div>
          <div>
            <Users className="h-6 w-6 text-primary" strokeWidth={1.3} />
            <h3 className="mt-3 text-xl text-foreground">Build with a team</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">Work alongside a community of disciples. Develop ideas, learn to steward resources and encourage one another toward a shared godly goal.</p>
          </div>
        </div>
      </section>

      <section id="remnant" className="px-6 py-16 md:py-24 border-b border-primary/20">
        <div className="max-w-5xl mx-auto text-center">
          <HandHeart className="mx-auto h-8 w-8 text-primary" strokeWidth={1.3} />
          <p className="mt-4 text-xs uppercase text-primary">The Remnant</p>
          <h2 className="mt-3 text-3xl md:text-4xl text-foreground">Decorum Lifestyle is the Remnant.</h2>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground">A community of disciples — men and women pressing on toward a godly goal. We grow spiritually, physically, mentally and financially, not just for ourselves, but to become people who can build, lead and serve together.</p>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">The purpose reaches beyond the Simulation: the True Fast. To loosen burdens, share bread with the hungry and turn what we've been given into help for others.</p>
          <p className="mt-3 text-xs text-muted-foreground">Isaiah 58:6–7</p>
          <blockquote className="mt-8 text-2xl italic text-primary">“I press on toward the goal.”</blockquote>
          <p className="mt-3 text-xs uppercase text-muted-foreground">Philippians 3:14</p>
        </div>
      </section>

      <section id="contact" className="px-6 py-14 md:py-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center text-3xl md:text-4xl text-primary">Apply to Join</h2>
          <p className="mt-4 text-center text-lg text-muted-foreground">Tell Lance where you are and what you're working toward.</p>
          <p className="mt-3 text-center text-sm text-muted-foreground">An interview comes first. Membership is by invitation.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            {sent ? (
              <p role="status" className="text-center text-foreground py-6">Thank you. Lance will be in touch personally.</p>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-5">
                  <label className="block text-sm text-foreground">Name
                    <Input className="mt-2" autoComplete="name" placeholder="Your name" value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </label>
                  <label className="block text-sm text-foreground">Email
                    <Input className="mt-2" autoComplete="email" placeholder="Your email" type="email" required value={form.email} maxLength={255} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </label>
                </div>
                <label className="block text-sm text-foreground">What would you like to work toward?
                  <Textarea className="mt-2" placeholder="Tell Lance about your goals" rows={3} value={form.message} maxLength={2000} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </label>
                <div className="text-center">
                  <Button type="submit" disabled={busy} size="lg" className="uppercase rounded-sm">
                    {busy ? "Sending…" : "Submit Application"} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
        <footer className="mt-14 border-t border-primary/20 pt-6 flex flex-wrap justify-between gap-4 text-xs text-muted-foreground">
          <span>© 2026 Decorum Lifestyle. All rights reserved.</span>
          <span>Discipline · Stewardship · Legacy</span>
          <Link to="/auth" className="hover:text-primary">Member Sign In</Link>
        </footer>
      </section>
    </div>
  );
}
